// useTenantFromHost — hits GET /api/auth/v1/tenant-from-host with the
// browser's host as `X-Forwarded-Host`. Result drives the tenant store +
// branding-override CSS vars.
//
// Path moved 2026-05-06: was /api/lyse/v1/auth/tenant-from-host;
// auth surface now lives at the platform-level /api/auth/v1/* namespace
// (see .vibe/21_AUTH_NAMESPACE_RELOCATION.md). The `auth/` sub-resource
// segment was dropped because auth IS the namespace, not a sub-resource.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Tenant } from './types.js';
import { useAuthStore } from './store.js';
import { applyBranding } from './applyBranding.js';
import { getAuthBaseUrl } from './config.js';

class TenantNotFoundError extends Error {
  override readonly name = 'TenantNotFoundError';
}

async function fetchTenantFromHost(host: string): Promise<Tenant> {
  const res = await fetch(`${getAuthBaseUrl()}/tenant-from-host`, {
    method: 'GET',
    headers: { 'X-Forwarded-Host': host },
  });
  if (res.status === 404) {
    throw new TenantNotFoundError('unknown_tenant');
  }
  if (!res.ok) {
    throw new Error(`tenant-from-host failed: ${String(res.status)}`);
  }
  return (await res.json()) as Tenant;
}

export interface UseTenantFromHostResult {
  tenant: Tenant | null;
  /** True when the BE returned 404 — distinct from a network error. */
  unknownTenant: boolean;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  /**
   * Manually re-fire the tenant-from-host query. Used by the §State 6
   * network-error card's "Try again" button (B-009.1 — UX brief addendum
   * 2026-05-04). Resolves once the underlying query settles (success or
   * error). The promise type matches @tanstack/react-query's `refetch`
   * surface and intentionally never throws — the result is observable
   * via the next render's `isError` / `isPending`.
   */
  refetch: () => Promise<unknown>;
}

export function useTenantFromHost(): UseTenantFromHostResult {
  const host = typeof window !== 'undefined' ? window.location.host : 'localhost';
  const setTenant = useAuthStore((s) => s.setTenant);

  const query = useQuery<Tenant>({
    queryKey: ['auth', 'tenant-from-host', host],
    queryFn: () => fetchTenantFromHost(host),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, err) => {
      if (err instanceof TenantNotFoundError) return false;
      return failureCount < 1;
    },
  });

  const unknownTenant = query.error instanceof TenantNotFoundError;

  useEffect(() => {
    if (query.data) {
      setTenant(query.data);
      applyBranding(query.data);
    } else if (unknownTenant) {
      setTenant(null);
      applyBranding(null);
    }
  }, [query.data, unknownTenant, setTenant]);

  // Sticky-error semantics for the LoginPage state machine: once we have
  // landed on an error, a manual refetch (B-009.1's "Try again" button)
  // must NOT cause the §State 6 card to unmount and be replaced by the
  // skeleton. TanStack Query v5 transitions `status` back to `'pending'`
  // during a refetch on an errored query (because `data === undefined`),
  // so we can't lean on `query.isError`. Instead use `errorUpdatedAt > 0`
  // — once we've recorded any error, the §State 6 card stays mounted
  // and `isFetching` carries the in-flight signal to the retry button.
  const hasErroredAtLeastOnce = query.errorUpdatedAt > 0;
  return {
    tenant: query.data ?? null,
    unknownTenant,
    isPending: query.isPending && !unknownTenant && !hasErroredAtLeastOnce,
    isError: hasErroredAtLeastOnce && !unknownTenant && !query.data,
    error: query.error,
    refetch: query.refetch,
  };
}
